import { Checkbox } from '@components/ui/checkbox';
import {
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogSubmit,
  DialogTitle
} from '@components/ui/dialog';
import { type Dispatch, type SetStateAction, useState } from 'react';

import { newError } from '@/services/errors/errors';

type Props = {
  setOpen: Dispatch<SetStateAction<boolean>>;
  nuke: () => Promise<void>;
  isDraft: boolean;
  stats?: {
    actionCount: number;
    transitionCount: number;
    workflowCount: number;
  };
};

export const DeleteDialogContent = ({
  setOpen,
  nuke,
  isDraft,
  stats
}: Props) => {
  const [checks, setChecks] = useState({
    want: isDraft,
    delete: false
  });
  const canDelete = checks.want && checks.delete;

  const onCancel = () => {
    setChecks({ want: isDraft, delete: false });
    setOpen(false);
  };

  return (
    <DialogContent
      className="w-1/2 border border-destructive border-solid"
      onClick={e => e.stopPropagation()}
    >
      <DialogHeader>
        <DialogTitle>Are you sure about deleting this project?</DialogTitle>
        <DialogDescription>
          Deleting a process is completely irreversible.{' '}
          <strong>All data about the process will be lost.</strong>
        </DialogDescription>
      </DialogHeader>
      <section className="flex flex-col gap-8 p-4">
        {stats && (
          <section className="flex flex-col gap-2">
            <h3 className="font-medium text-base">Your process has:</h3>
            <ul className="ml-8 list-disc">
              {stats?.workflowCount > 0 && (
                <li>
                  <strong>{stats.workflowCount}</strong> workflow
                  {stats.workflowCount > 1 ? 's' : ''}
                </li>
              )}
              {stats?.actionCount > 0 && (
                <li>
                  <strong>{stats.actionCount}</strong> action
                  {stats.actionCount > 1 ? 's' : ''}
                </li>
              )}
              {stats?.transitionCount > 0 && (
                <li>
                  <strong>{stats.transitionCount}</strong> transition
                  {stats.transitionCount > 1 ? 's' : ''}
                </li>
              )}
            </ul>
          </section>
        )}
        <section className="flex flex-col gap-2">
          {!isDraft && (
            <div className="flex items-center space-x-3">
              <Checkbox
                checked={checks.want}
                id="want"
                onCheckedChange={checked =>
                  setChecks(prev => ({ ...prev, want: !!checked }))
                }
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    setChecks(prev => ({ ...prev, want: !prev.want }));
                  }
                }}
              />
              <label
                className="font-medium text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                htmlFor="want"
              >
                I want to delete the process in Trace (all traces will be
                deleted has well)
              </label>
            </div>
          )}
          <div className="flex items-center space-x-3">
            <Checkbox
              checked={checks.delete}
              id="delete-checkbox"
              onCheckedChange={checked =>
                setChecks(prev => ({ ...prev, delete: !!checked }))
              }
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  setChecks(prev => ({ ...prev, delete: !prev.delete }));
                }
              }}
            />
            <label
              className="font-medium text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              htmlFor="delete-checkbox"
            >
              I understand the consequences of this action
            </label>
          </div>
        </section>
      </section>
      <DialogFooter>
        <DialogCancel onClick={onCancel}>Cancel</DialogCancel>
        <DialogSubmit
          disabled={!canDelete}
          onClick={e => {
            if (!canDelete) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            nuke().catch(e => {
              newError('NUKE-1', e, true);
            });
            setOpen(false);
          }}
          variant="destructive"
        >
          Delete the project 💥
        </DialogSubmit>
      </DialogFooter>
    </DialogContent>
  );
};
